/* eslint-disable */
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import { bindActionCreators } from "redux";
import { Search, ChevronRight } from "lucide-react";
import Loader from "../../Components/Loader";
import { dataActionCreators } from "../../services/Actions";
import { GetTotal } from "../../services/Actions/dataAction";
import RenderHirePurchasePage from "../RenderHirePurchasePage";
import { GhanaMoneyDisplay } from "../../helpers/helper";
import CohortFilter, {
  COHORT,
  cohortQuery,
  FILTER_INPUT_CLASS,
  FILTER_SELECT_CLASS,
} from "../../Components/CohortFilter";

function MartFundDeduction() {
  const dispatch = useDispatch();
  const { GetAllFunds, ResetDataResponse } = bindActionCreators(dataActionCreators, dispatch);

  const [limit, setLimit] = useState(30);
  const [search, setSearch] = useState(null);
  // Members or UPNMG staff. Every GetAllFunds call below goes through
  // cohortQuery() so switching cohort cannot leave one call site behind and
  // desync the list from the filter.
  const [cohort, setCohort] = useState(COHORT.MEMBERS);
  const [itemOffset, setItemOffset] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const data = useSelector((state) => state.data);
  const { funds, paginate, response, totalFunds, total_fund } = data;

  useEffect(() => {
    setPageCount(Number(paginate?.total));
  }, [funds, paginate]);

  useEffect(() => {
    GetAllFunds(true, 1, limit, cohortQuery(search, cohort));
  }, []);

  // Also the cohort switch's refetch — the toggle has no handler of its own.
  // GetTotal goes with it so the Total tile cannot print a member figure over
  // a staff table.
  useEffect(() => {
    GetAllFunds(true, 1, limit, cohortQuery(search, cohort));
    dispatch(GetTotal(cohort));
  }, [limit, cohort]);

  useEffect(() => {
    if (response?.state === "SUCCESS") {
      toast.success(response?.message);
      setTimeout(() => ResetDataResponse(), 1500);
    } else if (response?.state === "ERROR") {
      toast.error(response?.message);
      setTimeout(() => ResetDataResponse(), 1500);
    }
  }, [response?.state]);

  const handlePageClick = (event) => {
    GetAllFunds(true, event.selected + 1, limit, cohortQuery(search, cohort));
    const newOffset = (event.selected * limit) % (paginate?.total || 1);
    setItemOffset(newOffset);
  };

  const onSearchTextChange = debounce((e) => {
    const val = e?.target?.value || null;
    setSearch(val);
    GetAllFunds(true, 1, limit, cohortQuery(val, cohort));
  }, 800);

  return (
    <RenderHirePurchasePage title="Fund Deductions">

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={16} />
          <input
            type="search"
            placeholder="Search by Staff ID..."
            className={FILTER_INPUT_CLASS}
            onChange={onSearchTextChange}
          />
        </div>
        <CohortFilter
          value={cohort}
          onChange={setCohort}
          label="Deductions for"
          variant="segmented"
        />
        <select
          className={FILTER_SELECT_CLASS}
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
        >
          {[30, 50, 70, 100, 150, 250, 500].map((n) => (
            <option key={n} value={n}>{n} per page</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-100 dark:border-gray-700 overflow-hidden">
        {/* Table header with total */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Fund Contributions
          </h3>
          {total_fund != null && (
            <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
              Total: <GhanaMoneyDisplay amount={total_fund} />
            </span>
          )}
        </div>

        {data?.isLoading ? (
          <div className="p-6">
            <Loader />
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 dark:bg-gray-700/50">
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Member</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Period</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Transaction</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Facility</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
              {funds?.length > 0 ? (
                funds.map((fund) => (
                  <tr key={fund?._id} className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          className="w-9 h-9 rounded-full object-cover border border-slate-200 dark:border-gray-600 shadow-sm"
                          src={fund?.user?.profile_image || "/images/dev/avatar1.png"}
                          alt=""
                        />
                        <div>
                          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 leading-tight">
                            {fund?.user?.name}
                          </p>
                          <p className="text-xs text-slate-400 dark:text-slate-500 font-mono">
                            {fund?.staff_id}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800">
                        &#8373;{fund?.deduction}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 font-medium">
                      {fund?.period}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 font-medium">
                      {fund?.transaction_type}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 font-medium">
                      {fund?.user?.facility}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-400 rounded-lg transition-all">
                        <ChevronRight size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-14 text-center text-sm text-slate-400 dark:text-slate-500">
                    {data?.isLoading ? "Loading..." : "No fund deductions found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Pagination */}
        {pageCount > 1 && (
          <div className="border-t border-slate-100 dark:border-gray-700">
            <ReactPaginate
              breakLabel="..."
              nextLabel="›"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="‹"
              renderOnZeroPageCount={null}
              containerClassName="flex items-center justify-center gap-1 py-4"
              pageLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              previousLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              nextLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              activeLinkClassName="!bg-indigo-600 !text-white shadow-sm"
            />
          </div>
        )}
      </div>

      <ToastContainer autoClose={3000} />
    </RenderHirePurchasePage>
  );
}

export default MartFundDeduction;
